import { useState } from 'react';
import { CheckCheck, Laptop, Smartphone, Tablet, Mail, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import type { TrackingEvent, TrackingSummary } from '@/types/email';

interface TrackingIndicatorProps {
  messageId: string;
  summary: TrackingSummary | null;
  events: TrackingEvent[];
  onOpen?: () => void;
  isLoading?: boolean;
  className?: string;
}

function getDeviceIcon(deviceType: string) {
  switch (deviceType) {
    case 'mobile':
      return <Smartphone className="h-4 w-4" />;
    case 'tablet':
      return <Tablet className="h-4 w-4" />;
    case 'desktop':
      return <Laptop className="h-4 w-4" />;
    default:
      return <Mail className="h-4 w-4" />;
  }
}

function formatTimeAgo(dateString: string) {
  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return dateString;
  }
}

function formatDateTime(dateString: string) {
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  } catch {
    return dateString;
  }
}

export function TrackingIndicator({
  messageId: _messageId,
  summary,
  events,
  onOpen,
  isLoading,
  className
}: TrackingIndicatorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && onOpen) {
      onOpen();
    }
  };

  const hasSummary = Boolean(summary);
  const totalOpens = summary?.totalOpens ?? 0;
  const realOpens = summary?.realOpens ?? 0;
  const lastOpenedAt = summary?.lastOpenedAt ?? null;
  const displayCount = realOpens > 0 ? realOpens : totalOpens;
  const hasOpens = displayCount > 0;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors",
            hasOpens
              ? "text-primary hover:bg-primary/10 dark:hover:bg-primary/20"
              : "text-muted-foreground/70 hover:bg-muted/50 hover:text-foreground",
            "cursor-pointer select-none",
            className
          )}
          onClick={(e) => {
            e.stopPropagation(); // Prevent email selection
          }}
        >
          <CheckCheck className={cn("h-3.5 w-3.5", hasOpens ? "stroke-[2.5px]" : "stroke-2")} />
          {hasOpens && <span>{displayCount > 99 ? '99+' : displayCount}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 border-border/50 shadow-xl"
        align="end"
        side="bottom"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b bg-muted/10">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tracking Activity
            </h4>
            {lastOpenedAt && (
              <span className="text-[10px] text-muted-foreground">
                Last: {formatTimeAgo(lastOpenedAt)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2">
             <div className="flex items-center gap-1.5">
                <span className="text-2xl font-light text-foreground">{totalOpens}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Opens</span>
             </div>
             {realOpens !== totalOpens && (
               <div className="flex items-center gap-1.5 border-l pl-3">
                  <span className="text-2xl font-light text-foreground">{realOpens}</span>
                  <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Unique</span>
               </div>
             )}
          </div>
        </div>

        <div className="max-h-[320px] overflow-y-auto">
          <div className="py-1">
            {isLoading && !hasSummary ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground/50">
                 <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">No activity yet</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Tracking is active</p>
              </div>
            ) : (
              events.map((event, index) => (
                <div
                  key={event.id || index}
                  className={cn(
                    "px-4 py-2.5 border-b border-border/40 last:border-0 hover:bg-accent/50 transition-colors",
                    !event.isLikelyReal && "opacity-60 bg-muted/20"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                         <span className="text-sm font-medium truncate text-foreground">
                           {event.inferredRecipient || 'Someone'}
                         </span>
                         {!event.isLikelyReal && (
                            <Badge variant="outline" className="text-[9px] px-1 h-3.5 leading-none border-caution-line text-caution-ink bg-caution-surface">
                              Bot
                            </Badge>
                         )}
                      </div>
                      
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {getDeviceIcon(event.deviceType)}
                        <span className="truncate">
                          {event.locationCity ? (
                             <>
                               {event.locationCity}
                               {event.locationCountry && <span className="opacity-60">, {event.locationCountry}</span>}
                             </>
                          ) : (
                            event.deviceName || event.emailClient || 'Unknown device'
                          )}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-[10px] text-muted-foreground whitespace-nowrap pt-0.5">
                      {formatDateTime(event.openedAt)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
